import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, BookOpen, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function MemoryBookRequestPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [entityId, setEntityId] = useState(sp.get("entity_id") || sp.get("athlete") || "");
  const [entityType, setEntityType] = useState(sp.get("entity_type") || "athlete");
  const [entityName, setEntityName] = useState("");
  const [periodYear, setPeriodYear] = useState("");
  const [context, setContext] = useState("");
  const [notes, setNotes] = useState("");
  const [road2027, setRoad2027] = useState(true);
  const [copf, setCopf] = useState(false);

  // V11.4 — Si entity_type est fourni via URL (venant du cockpit fédé), on
  // pré-charge le nom de l'entité pour affichage.
  useEffect(() => {
    if (!entityId || entityType === "athlete") return;
    const endpoint = entityType === "federation" ? "federations"
                   : entityType === "club" ? "clubs" : null;
    if (!endpoint) return;
    api.get(`/${endpoint}/${entityId}`)
      .then((r) => setEntityName(r.data?.name || ""))
      .catch(() => {});
  }, [entityId, entityType]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // Récupérer les athlètes dont l'utilisateur est owner
        const res = await api.get("/athletes/mine");
        if (!alive) return;
        const list = res.data?.items || res.data || [];
        setAthletes(list);
        if (!entityId && list.length === 1) setEntityId(list[0].id);
      } catch {
        setAthletes([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [entityId]);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4" data-testid="memorybook-request-not-logged">
        <h1 className="text-2xl font-display font-semibold">Demander un livret AITO</h1>
        <p className="text-sm text-muted-foreground">
          Connectez-vous pour demander un livret éditorial à partir de votre fiche AITO.
        </p>
        <Button onClick={() => nav("/connexion?next=/memoires/nouvelle-demande")}>Me connecter</Button>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!entityId) {
      toast.error("Sélectionnez une fiche athlète");
      return;
    }
    setSubmitting(true);
    try {
      const memoryTypeMap = { athlete: "ATHLETE", federation: "FEDERATION", club: "CLUB" };
      const payload = {
        memory_type: memoryTypeMap[entityType] || "ATHLETE",
        entity_type: entityType,
        entity_id: entityId,
        period_year: parseInt(periodYear, 10) || null,
        road_to_2027: entityType === "athlete" ? road2027 : false,
        context: context || null,
        additional_notes: notes || null,
        request_copf_approval: copf,
      };
      const res = await api.post("/memory-books/requests", payload);
      toast.success("Demande envoyée à l'équipe AITO");
      nav("/mes-memoires", { state: { justCreated: res.data?.id } });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la demande");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6 space-y-6" data-testid="memorybook-request-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Retour
        </Button>
      </div>

      <Card className="aito-card-hover">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
            Demander un livret AITO
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            L'équipe AITO consolide vos données (résultats, compétitions, actualités) et prend en
            charge la rédaction éditoriale. Vous recevez un PDF premium une fois finalisé.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label>{entityType === "federation" ? "Fédération" : entityType === "club" ? "Club" : "Fiche athlète"}</Label>
              {entityType !== "athlete" ? (
                <div className="rounded border p-3 bg-secondary/40 flex items-center gap-2 text-sm" data-testid="memorybook-request-entity-prefill">
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="font-medium">{entityName || "…"}</div>
                    <div className="text-[11px] text-muted-foreground">Livret <strong>{entityType.toUpperCase()}</strong> · Traitement premium par l'équipe AITO</div>
                  </div>
                </div>
              ) : loading ? (
                <div className="text-sm text-muted-foreground">Chargement des fiches…</div>
              ) : athletes.length === 0 ? (
                <div className="text-sm text-muted-foreground italic">
                  Aucune fiche associée à votre compte. Créez d'abord votre fiche athlète.
                </div>
              ) : (
                <Select value={entityId} onValueChange={setEntityId}>
                  <SelectTrigger data-testid="memorybook-request-entity-select">
                    <SelectValue placeholder="Sélectionnez une fiche" />
                  </SelectTrigger>
                  <SelectContent>
                    {athletes.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.full_name} {a.main_discipline ? `— ${a.main_discipline}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="period_year">Année (optionnelle)</Label>
              <Input
                id="period_year"
                type="number"
                min={2000}
                max={2030}
                placeholder="Tout le parcours par défaut"
                value={periodYear}
                onChange={(e) => setPeriodYear(e.target.value)}
                data-testid="memorybook-request-period-year"
              />
              <p className="text-xs text-muted-foreground">
                Laissez vide pour un livret couvrant l'ensemble du parcours. Renseignez une année pour un bilan annuel ciblé.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="context">Objectif / usage prévu</Label>
              <Input
                id="context"
                placeholder="Ex : dossier de sponsoring, mémoire personnelle, présentation fédération…"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                data-testid="memorybook-request-context"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes complémentaires</Label>
              <Textarea
                id="notes"
                rows={3}
                placeholder="Faits particuliers à mettre en avant, contexte, absences à mentionner…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="memorybook-request-notes"
              />
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label>Focus Jeux du Pacifique 2027</Label>
                <p className="text-xs text-muted-foreground">
                  Ajoute une section dédiée Jeux du Pacifique 2027 si votre fiche y est engagée.
                </p>
              </div>
              <Switch
                checked={road2027}
                onCheckedChange={setRoad2027}
                data-testid="memorybook-request-road2027-toggle"
              />
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label>Demander un agrément COPF</Label>
                <p className="text-xs text-muted-foreground">
                  Le COPF pourra valider le livret pour lui apposer son agrément institutionnel.
                </p>
              </div>
              <Switch
                checked={copf}
                onCheckedChange={setCopf}
                data-testid="memorybook-request-copf-toggle"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting || !entityId}
              className="w-full"
              data-testid="memorybook-request-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Envoyer la demande
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
